import { formatMatterInlineLabelFromData } from './matter-display';

/**
 * GET /matters/search/ URL. With `imputationId`, the server also applies that
 * category's Matter rule, if it has one, on top of the firm-wide hidden list
 * (XPEN-3757); it goes before `text` so a reader of `split('text=')[1]` still
 * gets the typed text.
 */
export function matterSearchUrl(text, { imputationId, limit } = {}) {
  // Keep text last for existing callers that parse it from the URL.
  const scope = [matterScopeParams(imputationId), limit == null ? null : `limit=${encodeURIComponent(limit)}`].filter(Boolean).join('&');
  return `/matters/search/?${scope ? `${scope}&` : ''}text=${encodeURIComponent(text ?? '')}`;
}

/**
 * The `imputation_id=<id>` parameter that scopes a Matter search to a category's
 * rule, or undefined without a category (XPEN-3760). AutoCompleteField takes it
 * as `searchParams`; matterSearchUrl builds its URL with it.
 */
export function matterScopeParams(imputationId) {
  return imputationId ? `imputation_id=${encodeURIComponent(imputationId)}` : undefined;
}

/**
 * A category's Matter rule shape (XPEN-3757): null unless the mode is "only" or
 * "except"; `matter_numbers` is the stored array, or [] when it is not one.
 */
export function normalizeMatterRestriction(raw) {
  if (!raw || typeof raw !== 'object' || (raw.mode !== 'only' && raw.mode !== 'except')) return null;
  return {
    mode: raw.mode,
    matter_numbers: Array.isArray(raw.matter_numbers) ? raw.matter_numbers : [],
  };
}

// Follows xpens-api utils/matter_restriction.py normalize_shape: a list it would
// refuse makes the server apply no rule. Lengths here count UTF-16 units (Python
// counts code points); the service only stores lists it has already normalized.
const MAX_MATTER_NUMBERS = 500;
const MAX_MATTER_NUMBER_LENGTH = 128;

/** The trimmed numbers the server applies for this rule, or null for no limit. */
function appliedMatterNumbers(restriction) {
  if (!restriction || restriction.matter_numbers.length === 0) return null;
  const numbers = [];
  for (const raw of restriction.matter_numbers) {
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const number = raw.trim();
    if (number.length > MAX_MATTER_NUMBER_LENGTH) return null;
    if (!numbers.includes(number)) numbers.push(number);
  }
  return numbers.length > MAX_MATTER_NUMBERS ? null : numbers;
}

/** Whether a stored rule means "no rule of its own": absent, null or mode "all". */
function hasNoOwnRule(raw) {
  return raw === undefined || raw === null
    || (typeof raw === 'object' && !Array.isArray(raw) && raw.mode === 'all');
}

/**
 * The rule a picked category row applies (XPEN-3759), mirroring the Matter search
 * of xpens-api (matter_restriction_of): none for a travel type or a subcategory
 * of one; else the row's own rule; else, when it has none of its own, the rule of
 * its category `parentRow` (XPEN-3781). A category with subcategories applies its
 * own. A stored rule the server would refuse (an unknown mode, not an object)
 * applies none and does not fall back. Without `parentRow` (a category, or a
 * subcategory whose category this user's list does not hold) there is nothing to
 * follow; the server, which reads the category itself, then decides.
 */
export function appliedMatterRule(row, { parentRow = null } = {}) {
  if (!row) return null;
  if (row.imputation_catalog === 'displacement' || parentRow?.imputation_catalog === 'displacement') return null;
  if (normalizeMatterRestriction(row.matter_restriction)) return row.matter_restriction;
  if (!hasNoOwnRule(row.matter_restriction) || !parentRow) return null;
  return normalizeMatterRestriction(parentRow.matter_restriction) ? parentRow.matter_restriction : null;
}

/**
 * Whether a category's rule allows this Matter (XPEN-3759). `matter` is a select
 * option or a stored number not yet resolved; a resolved Matter counts as listed
 * by its number or its alternate number (XPEN-3080), a plain string as is. A list
 * the server's normalize_shape would refuse (empty, a non-text or blank number,
 * a number over 128 characters, more than 500 numbers) means no limit; listed
 * numbers are compared trimmed. The server's save check (XPEN-3761) decides: it
 * also resolves a stored alternate number to its Matter and ignores case, which
 * this check does not do for a plain string.
 */
// The server's search matches the rule on the Matter number only; this check also
// counts the alternate number of a resolved Matter, as the save check (XPEN-3761) does.
export function matterAllowedByRestriction(rule, matter) {
  const restriction = normalizeMatterRestriction(rule);
  const numbers = appliedMatterNumbers(restriction);
  if (!numbers) return true;
  const identifiers = typeof matter === 'string'
    ? [matter]
    : [matter?.value?.number, matter?.value?.altNumber];
  const forms = identifiers.filter((id) => id != null && id !== '').map(String);
  if (forms.length === 0) return true;
  const listed = forms.some((id) => numbers.includes(id));
  return restriction.mode === 'only' ? listed : !listed;
}

// Both casings: a stored expense may carry the legacy `MatterID`, and the
// server's save copies a header `MatterID` into a billable line whose `MatterId`
// is empty (xpens-api utils.cascade_billing_fields_to_line).
const MATTER_KEYS = ['MatterId', 'MatterID'];

/** A copy of `holder` with every Matter key the rule does not allow set to '', or `holder` itself. */
function clearDisallowedMatterKeys(holder, rule) {
  let next = holder;
  MATTER_KEYS.forEach((key) => {
    // Trimmed, as the listed numbers are; a blank Matter is never "not listed".
    const matter = holder?.[key] == null ? '' : String(holder[key]).trim();
    if (!matter || matterAllowedByRestriction(rule, matter)) return;
    next = { ...next, [key]: '' };
  });
  return next;
}

/**
 * The expense json with every Matter the category's rule does not allow cleared
 * (XPEN-3760): the header and each line get '', and a stored MatterSplits that
 * names a disallowed Matter becomes null. The editor never edits a split row by
 * row, and only an explicit null clears a stored split on save (XPEN-3653-005).
 * A missing Matter is never treated as "not listed". Returns `json` itself when
 * nothing changes.
 */
export function clearMattersNotAllowedInJson(json, rule) {
  if (!json) return json;
  let next = clearDisallowedMatterKeys(json, rule);
  if (Array.isArray(json.ListItem)) {
    const items = json.ListItem.map((item) => clearDisallowedMatterKeys(item, rule));
    if (items.some((item, i) => item !== json.ListItem[i])) next = { ...next, ListItem: items };
  }
  if (Array.isArray(json.MatterSplits) && json.MatterSplits.some((split) => clearDisallowedMatterKeys(split, rule) !== split)) {
    next = { ...next, MatterSplits: null };
  }
  return next;
}

export function matterFromSearchHit(hit) {
  if (!hit || typeof hit !== 'object') return null;
  const number = hit.name ?? hit.number ?? '';
  return { ...hit, number };
}

export function findExactMatterSearchHit(hits, number) {
  if (!Array.isArray(hits) || number == null || number === '') return null;
  const needle = String(number);
  for (const hit of hits) {
    const mapped = matterFromSearchHit(hit);
    if (!mapped) continue;
    if (String(mapped.number) === needle || String(mapped.altNumber ?? '') === needle) {
      return mapped;
    }
  }
  return null;
}

export function toMatterSelectOption(data, hit) {
  const value = matterFromSearchHit(hit);
  if (!value?.number) return null;
  return {
    label: formatMatterInlineLabelFromData(data, value),
    value,
  };
}
